import type { IBasicPostData } from '@gik/api/activities/posts';
import { useUser } from '@gik/api/users/user';
import type { BaseUserWithAvatar } from '@gik/core/models/gik/User';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { AttachmentPreview } from '@gik/stream/components/Posts/AttachmentPreview';
import type { PostActivity } from '@gik/stream/types/Stream';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import { parseStreamContent } from '@gik/stream/utils/parseStreamContent';
import { Button } from '@gik/ui/Button';
import { Popover } from '@gik/ui/Popover';
import { PopoverMenu } from '@gik/ui/PopoverMenu';
import { SvgIcon } from '@gik/ui/SvgIcon';
import { Text } from '@gik/ui/Text/Text';
import { UI } from '@gik/ui/UIManager';
import { openUserInfoModal } from '@gik/user-profile/components/UserInfoModal/UserInfoModal';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon';
import PencilIcon from '@heroicons/react/solid/PencilIcon';
import TrashIcon from '@heroicons/react/solid/TrashIcon';
import moment from 'moment';
import React from 'react';
import { Avatar } from '../../../ui/Avatar';
import type { ReactionEmojiKey } from '../../../ui/OpenMoji';
import { StreamReactions } from './StreamReactions';

export type StreamPostProps = {
  inkindRouteId: string;
  activity: PostActivity;
  participants?: StreamParticipant[];
  error?: string;
  isPageOwnerOrOrganizer?: boolean;
  disabled?: boolean;
  isTemp?: boolean;
  highlightedCommentId?: string;
  onReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onDeleteReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCreateRetry?: (id: string, data: IBasicPostData) => void;
  onUpdateRetry?: (id: string, data: IBasicPostData) => void;
  onDeleteRetry?: (id: string) => void;
} & UIComponent;

function StreamPostComp({
  inkindRouteId,
  children,
  className,
  activity,
  participants,
  error,
  isPageOwnerOrOrganizer,
  disabled,
  isTemp,
  highlightedCommentId,
  onReaction,
  onDeleteReaction,
  onEdit,
  onDelete,
  onCreateRetry,
  onUpdateRetry,
  onDeleteRetry,
  ...otherProps
}: React.PropsWithChildren<StreamPostProps>): React.ReactElement {
  const bem = useBemCN('stream-post');

  const content = activity.post?.message;

  const [allParticipants, setAllParticipants] = React.useState(participants);

  const [moreOpen, setMoreOpen] = React.useState<boolean>(false);
  const userId = useUserStore(state => state.id);
  const { data: postOwner } = useUser(activity.post?.userId);

  const media = React.useMemo(() => {
    return (
      Object.keys(activity?.media || {}).map(key => {
        return activity.media[key];
      }) || []
    );
  }, [activity]);

  React.useEffect(() => {
    if (!postOwner) return;

    setAllParticipants(
      participants.concat([
        {
          id: postOwner?.id,
          display: postOwner?.fullName,
          userAvatarThumbSmallURL: postOwner?.avatarThumbSmallURL,
          userAvatarThumbMediumURL: postOwner?.avatarThumbMediumURL,
        },
      ])
    );
  }, [participants, postOwner]);

  const parsedContent = React.useMemo(() => parseStreamContent(content, participants), [content, participants]);

  const backgroundColor: string = activity.post?.backgroundColor || 'white';

  const contentLength = parsedContent.length;

  const handleEdit = React.useCallback(() => {
    setMoreOpen(false);
    onEdit?.();
  }, [onEdit]);

  const handleDelete = React.useCallback(async () => {
    setMoreOpen(false);

    const response = await UI.confirm('Are you sure you want to delete this message ?', {
      title: 'Confirm Delete',
      okText: 'Delete',
      okButtonProps: {
        variant: 'danger',
      },
    });
    if (!response) return;

    onDelete?.();
  }, [onDelete]);

  function renderError(error: string) {
    switch (error) {
      case 'create_fail':
        return (
          <span>
            <span>Failed to create post.</span>{' '}
            <Button
              variant="danger-link"
              onClick={() => onCreateRetry(activity.id, { message: activity.post.message })}
            >
              Try again
            </Button>
          </span>
        );
      case 'update_fail':
        return (
          <span>
            <span>Failed to update post.</span>{' '}
            <Button
              variant="danger-link"
              onClick={() => onUpdateRetry(activity.id, { message: activity.post.message as string })}
            >
              Try again
            </Button>
          </span>
        );
      case 'delete_fail':
        return (
          <span>
            <span>Failed to delete post.</span>{' '}
            <Button variant="danger-link" onClick={() => onDeleteRetry(activity.id)}>
              Try again
            </Button>
          </span>
        );
      case 'undelete_fail':
        return (
          <span>
            <span>Failed to undelete post.</span>{' '}
            <Button variant="danger-link" onClick={() => onDeleteRetry(activity.id)}>
              Try again
            </Button>
          </span>
        );
      default:
        return 'unknown error';
    }
  }

  let user: BaseUserWithAvatar = {
    fullName: '-',
    avatarThumbSmallURL: null,
    avatarThumbMediumURL: null,
    avatarURL: null,

    id: activity.post?.userId,
    emailAddress: null,
  };

  if (postOwner) {
    user = postOwner;
  }

  return (
    <div
      {...bem(
        null,
        [
          { [`color-${backgroundColor}`]: backgroundColor },
          { styled: backgroundColor !== 'white' && backgroundColor },
          { ['empty-text']: contentLength === 0 },
        ],
        className
      )}
      data-id={activity.id}
      {...otherProps}
    >
      <div {...bem('header')}>
        <main {...bem('header-main')}>
          <Avatar imageUrl={user.avatarThumbSmallURL} onClick={() => openUserInfoModal(user)} />
          <div {...bem('content')}>
            <a {...bem('username')} onClick={() => openUserInfoModal(user)}>
              <Text>{user.fullName}</Text>
            </a>
            <br />
            <span {...bem('date')}>{moment.utc(activity.time).fromNow()}</span>
          </div>
        </main>
        <aside {...bem('header-aside')}>
          {!isTemp && (isPageOwnerOrOrganizer || activity.post?.userId === userId) && (
            <Popover
              noPad
              placement="bottom-start"
              {...bem('more-menu')}
              trigger={
                <Button disabled={disabled} variant="white" circle size="sm" customIconSize {...bem('btn-more')}>
                  <SvgIcon Icon={DotsHorizontalIcon} size="base" />
                </Button>
              }
              isOpen={moreOpen}
              triggerOnClick={() => {
                if (disabled) return;
                setMoreOpen(true);
              }}
              onClose={() => setMoreOpen(false)}
            >
              <PopoverMenu>
                {activity.post?.userId === userId && (
                  <PopoverMenu.Item onClick={handleEdit} icon={PencilIcon}>
                    Edit
                  </PopoverMenu.Item>
                )}

                <PopoverMenu.Item onClick={handleDelete} icon={TrashIcon}>
                  Delete
                </PopoverMenu.Item>
              </PopoverMenu>
            </Popover>
          )}
        </aside>
      </div>
      <div {...bem('content-wrapper')}>
        <div {...bem('text-wrapper')}>
          <Text {...bem('text')}>{parsedContent}</Text>
        </div>
        {media?.length > 0 && <AttachmentPreview {...bem('media')} media={media} />}
      </div>
      {error && <span {...bem('error')}>{renderError(error)}</span>}

      {!isTemp && (
        <StreamReactions
          key={activity.id}
          activity={activity}
          isPageOwnerOrOrganizer={isPageOwnerOrOrganizer}
          disabled={disabled}
          onReaction={onReaction}
          onDeleteReaction={onDeleteReaction}
          participants={allParticipants}
          highlightedCommentId={highlightedCommentId}
        />
      )}
    </div>
  );
}

export const StreamPost = withComponentErrorBoundary(StreamPostComp);
